/**
 * Client for the public BrainBank Dropzone endpoints.
 *
 * Deliberately not the `api` axios instance from apiService: that one injects
 * a bearer token from sessionStorage and re-authenticates when it has expired,
 * and the upload page has no user. These calls carry only the link token, in a
 * header so it never lands in a request path, and send no referrer so the
 * token in the page URL never travels on.
 */
import { DropzoneGate, DropzoneInfo, DropzoneUploadGrant } from "../types";

const endpoint = process.env.REACT_APP_API_ENDPOINT || "";
export const TOKEN_HEADER = "X-Dropzone-Token";
export const PASSCODE_HEADER = "X-Dropzone-Passcode";

export const isGate = (data: DropzoneInfo | DropzoneGate): data is DropzoneGate =>
  !("label" in data);

export class DropzoneError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "DropzoneError";
    this.status = status;
  }
}

const readDetail = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.json();
    if (body && typeof body.detail === "string" && body.detail.trim()) return body.detail;
  } catch {
    // no JSON body
  }
  return fallback;
};

const call = async (
  path: string,
  token: string,
  init: RequestInit = {},
  passcode?: string
): Promise<Response> => {
  let response: Response;
  try {
    response = await fetch(`${endpoint}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        [TOKEN_HEADER]: token,
        ...(passcode ? { [PASSCODE_HEADER]: passcode } : {}),
        ...(init.headers || {}),
      },
      referrerPolicy: "no-referrer",
      credentials: "omit",
    });
  } catch (error) {
    throw new DropzoneError(0, "Could not reach the upload service. Check your connection and try again.");
  }
  if (!response.ok) {
    const fallback =
      response.status === 503
        ? "The upload service is busy. Try again in a moment."
        : response.status === 429
        ? "Too many requests. Try again in a minute."
        : response.status === 401
        ? "Wrong passcode."
        : "This upload link is not valid.";
    throw new DropzoneError(response.status, await readDetail(response, fallback));
  }
  return response;
};

/** Without the right passcode a protected link answers only `{passcode_required: true}`. */
export const getDropzone = async (
  token: string,
  passcode?: string
): Promise<DropzoneInfo | DropzoneGate> => {
  const response = await call("/public/dropzone", token, {}, passcode);
  return response.json();
};

export const requestDropzoneUpload = async (
  token: string,
  name: string,
  size: number,
  passcode?: string
): Promise<DropzoneUploadGrant> => {
  const response = await call(
    "/public/dropzone/files",
    token,
    { method: "POST", body: JSON.stringify({ name, size }) },
    passcode
  );
  return response.json();
};

export const completeDropzoneUpload = async (
  token: string,
  fileId: string,
  passcode?: string
): Promise<void> => {
  await call(
    `/public/dropzone/files/${encodeURIComponent(fileId)}/complete`,
    token,
    { method: "POST" },
    passcode
  );
};

/**
 * Multipart POST to the presigned S3 URL. Every signed field goes first and the
 * file last: S3 ignores anything after the file part. A failure with no HTTP
 * status is a network or CORS failure, surfaced as status 0 so the page can
 * tell the client it is not their fault.
 */
export const uploadToS3 = (
  url: string,
  fields: Record<string, string>,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => form.append(key, value));
    form.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new DropzoneError(xhr.status, s3ErrorMessage(xhr.status, xhr.responseText)));
      }
    });
    xhr.addEventListener("error", () => reject(new DropzoneError(0, "Upload blocked.")));
    xhr.addEventListener("timeout", () => reject(new DropzoneError(0, "Upload timed out.")));
    xhr.open("POST", url);
    xhr.send(form);
  });

const s3ErrorMessage = (status: number, body: string): string => {
  if (/EntityTooLarge/.test(body)) return "This file is over the size limit.";
  if (/EntityTooSmall/.test(body)) return "This file is empty.";
  if (/Policy Condition failed|AccessDenied/.test(body)) return "The upload was rejected. Try again.";
  if (status === 403) return "The upload URL has expired. Try again.";
  return "The upload failed. Try again.";
};
